import React, { useEffect } from 'react';
import { Box, Paper, Typography, Divider, ThemeProvider } from '@mui/material';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DnsResolutionTest from '../../components/DnsResolutionTest';
import DnsRecordsLookup from './DnsRecordsLookup';
import DnsReverseLookup from './DnsReverseLookup';
import lightTheme from '../../themes/lightTheme';

/**
 * Página /dns_tools — Ferramentas DNS expandidas inline.
 * Cada ferramenta numa Paper branca com header + corpo do componente
 * em modo `inline`.
 */
function ToolSection({ icon: Icon, title, description, children }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 3,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Icon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500 }}>
                    {title}
                </Typography>
            </Box>
            {description && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {description}
                </Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            {children}
        </Paper>
    );
}

export default function ToolsPage() {
    useEffect(() => {
        document.title = 'IPV7 Tools - Ferramentas DNS';
    }, []);

    return (
        <ThemeProvider theme={lightTheme}>
            <Box sx={{
                bgcolor: 'background.default',
                color: 'text.primary',
                minHeight: 'calc(100vh - 100px)',
                p: { xs: 1, md: 2 },
            }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                        Ferramentas DNS
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Utilitários de resolução DNS — selecione os servidores (nós configurados,
                        DNS público ou IPs customizados) e execute as consultas.
                    </Typography>
                </Box>

                <ToolSection
                    icon={NetworkCheckIcon}
                    title="Teste de Resolução"
                    description="Consulta o domínio nos servidores selecionados. Detecta vazamentos comparando com DNS de referência."
                >
                    <DnsResolutionTest inline />
                </ToolSection>

                <ToolSection
                    icon={TravelExploreIcon}
                    title="Lookup de Records"
                    description="Consulta múltiplos tipos de records (A, AAAA, MX, NS, TXT, CNAME, SOA, SRV)."
                >
                    <DnsRecordsLookup inline />
                </ToolSection>

                <ToolSection
                    icon={SwapHorizIcon}
                    title="Reverse DNS (PTR)"
                    description="Hostname associado a um IP. Aceita IPv4 e IPv6 — converte automaticamente pra in-addr.arpa / ip6.arpa."
                >
                    <DnsReverseLookup inline />
                </ToolSection>
            </Box>
        </ThemeProvider>
    );
}
